import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { companyName, companyLegalName, supportEmail } from "@/lib/companyBrand";
import { clientFromSearch, linkForClient } from "@/lib/guideClient";

const Section = ({ title, children }) => (
  <div className="py-8 border-b border-white/5 last:border-0">
    <h2
      className="text-base font-medium text-white mb-4"
      style={{ fontFamily: "Outfit, sans-serif" }}
    >
      {title}
    </h2>
    <div className="text-sm text-zinc-400 leading-7 space-y-3">{children}</div>
  </div>
);

const Highlight = ({ children }) => (
  <span className="text-zinc-300">{children}</span>
);

/** The Privacy Policy, linked so that a reader from the iOS app keeps its mode. */
const PrivacyLink = () => {
  const client = clientFromSearch(useLocation().search);
  return (
    <a href={linkForClient("/privacy", client)} className="text-[var(--accent-yellow-text)] hover:underline">
      Privacy Policy
    </a>
  );
};

const TermsPage = () => (
  <PublicLayout>
    <main className="relative z-10 px-4 md:px-8 pt-12 md:pt-20 pb-20 md:pb-32 max-w-2xl mx-auto safe-area-padding">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      >
        {/* Header */}
        <div className="mb-12">
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-4">Legal</p>
          <h1
            className="text-3xl md:text-4xl font-light tracking-tight mb-5"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Terms of Service
          </h1>
          <p className="text-sm text-zinc-500 mb-6">
            Last updated November 2, 2026 &middot; Hans terms effective June 13, 2026;
            Scheduler coverage effective October 23, 2026
          </p>
          <p className="text-sm text-zinc-400 leading-7">
            These Terms of Service ("Terms") govern your access to and use of Hans and
            Kalver Scheduler (together, the "Service"), operated by {companyLegalName()}
            {" "}("we," "us," or "our"). By creating an account or using the Service, you
            agree to these Terms. Provisions about recordings, AI-generated content,
            and student portals apply when you use those Hans features.
          </p>
        </div>

        {/* Sections */}
        <Section title="1. The Service">
          <p>
            Hans is a software application that helps private music instructors record,
            transcribe, and summarize lessons using artificial intelligence. It also provides
            a student portal for reviewing lesson summaries, logging practice, and generating
            portfolio content.
          </p>
          <p>
            Kalver Scheduler provides online booking, staff calendars, availability,
            appointment management, payment processing through connected payment
            providers, reminders, and optional connections to external calendars.
            The business offering an appointment is responsible for delivering its
            lessons or other services.
          </p>
          <p>
            We reserve the right to modify, suspend, or discontinue any part of the Service
            at any time. We'll give reasonable notice for material changes.
          </p>
        </Section>

        <Section title="2. Eligibility">
          <p>
            You must be at least 18 years old to create an instructor or business
            administrator account, or to make a booking on behalf of a child. If you are
            using the student portal, you may use it with the permission of a parent or
            guardian if you are under 18. By using the Service, you represent that you meet these
            requirements.
          </p>
        </Section>

        <Section title="3. Accounts">
          <p>
            <Highlight>Account access.</Highlight> You are responsible for maintaining
            the security of your account credentials. Notify us immediately at{" "}
            <a href={`mailto:${supportEmail()}`} className="text-[#f4cf08] hover:underline">
              {supportEmail()}
            </a>{" "}
            if you suspect unauthorized access.
          </p>
          <p>
            <Highlight>Student accounts.</Highlight> Student portal accounts are created by
            accepting an invitation from a connected instructor. Students are responsible for
            keeping their login credentials confidential.
          </p>
          <p>
            <Highlight>One account per person.</Highlight> You may not share accounts or
            create accounts on behalf of others without their knowledge.
          </p>
        </Section>

        <Section title="4. Subscriptions and billing">
          <p>
            Hans offers free and paid subscription tiers. Paid plans are billed monthly or
            annually as selected at purchase.
          </p>
          <p>
            <Highlight>Web subscriptions</Highlight> are processed by Stripe. By subscribing,
            you authorize us to charge your payment method on a recurring basis until you cancel.
          </p>
          <p>
            <Highlight>iOS subscriptions</Highlight> are processed by Apple through In-App
            Purchase. Billing, cancellation, and refunds for iOS subscriptions are governed
            by Apple's terms and managed through your Apple ID account settings.
          </p>
          <p>
            <Highlight>Cancellation.</Highlight> You may cancel at any time. Access continues
            until the end of the current billing period. We do not offer prorated refunds for
            partial periods, except where required by law.
          </p>
          <p>
            <Highlight>Price changes.</Highlight> We'll give at least 30 days notice before
            increasing prices on active subscriptions.
          </p>
        </Section>

        <Section title="Booking and calendar connections in Kalver Scheduler">
          <p>
            <Highlight>Appointments and payments.</Highlight> The business you book with
            sets its prices, availability, payment requirements, and cancellation,
            rescheduling, and refund policies. Review those details before booking.
            Appointment charges are separate from Hans subscriptions. Payment details
            are processed by the business's connected payment provider.
          </p>
          <p>
            A reservation awaiting required payment is not a paid, confirmed appointment.
            Follow the checkout instructions and any payment deadline shown; an unpaid
            reservation may expire and release the time slot. Contact the business for
            questions about its service, cancellations, or refunds.
          </p>
          <p>
            <Highlight>External calendars.</Highlight> Connecting Google Calendar or
            another supported calendar is optional. Only connect accounts and calendars
            you are authorized to use. According to the permissions you grant and the
            settings you select, Scheduler reads calendar information and busy times,
            and creates or updates events for appointments. Calendar sharing settings
            determine who can see those events in the external service.
          </p>
          <p>
            You can disconnect a calendar in Scheduler or revoke access with the calendar
            provider. Disconnecting does not automatically delete events already created
            in that calendar. Synchronization depends on the external provider and may
            be delayed or interrupted. Review your calendar and booking confirmations
            when arranging appointments. Our <PrivacyLink />
            {" "}describes how Scheduler uses, protects, and retains connected calendar data.
          </p>
          <p>
            Booking a lesson or connecting a calendar does not grant consent to record
            a lesson. Hans recording consent remains a separate process.
          </p>
        </Section>

        <Section title="5. Your content">
          <p>
            You retain ownership of all content you create or upload — lesson recordings,
            notes, student information, portfolio materials, and scheduling information.
          </p>
          <p>
            By using the Service, you grant us a limited, non-exclusive license to process your
            content solely to provide the Service (e.g., transcription, AI analysis, email
            delivery, booking, and calendar synchronization). We do not use your content
            to train AI models or for any other purpose.
          </p>
          <p>
            <Highlight>Full lesson recordings</Highlight> are deleted after processing succeeds
            or permanently fails. Audio awaiting retry is retained temporarily, with a seven-day
            cleanup backstop. Consented practice clips are kept separately for up to 30 days.
          </p>
        </Section>

        <Section title="6. Acceptable use">
          <p>You agree not to:</p>
          <ul className="space-y-2 mt-2">
            {[
              "Use Hans to record individuals without their knowledge or consent",
              "Upload content that is illegal, harmful, or violates others' rights",
              "Attempt to reverse-engineer, scrape, or extract data from the Service",
              "Use the Service to train AI models or for competitive intelligence",
              "Share your account credentials or allow unauthorized access",
              "Circumvent any security, rate limit, or access control in the Service",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3">
                <span className="w-1 h-1 rounded-full bg-zinc-600 shrink-0 mt-2.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </Section>

        {/* Recording consent + indemnification — drafted plain language;
            attorney review recommended. */}
        <Section title="7. Recording consent and your responsibilities">
          <p>
            Hans records lesson audio on your behalf. <Highlight>You represent and
            warrant</Highlight> that, before recording any lesson, you have obtained — and
            will maintain records of — every consent required by applicable law to record
            the people in the room, including the student's own consent for adult students,
            a parent or guardian's consent for students under 18, and{" "}
            <Highlight>verifiable parental consent</Highlight> for children under 13 as
            required by the Children's Online Privacy Protection Act (COPPA).
          </p>
          <p>
            Hans provides built-in consent tooling — consent request emails, a logged
            consent ledger, and recording locks — to help you meet these obligations.
            Recording is disabled for a student until consent is recorded, and you agree
            not to circumvent these controls.
          </p>
          <p>
            <Highlight>Indemnification.</Highlight> You agree to indemnify, defend, and hold
            harmless {companyLegalName()} and its officers, employees, and agents from and against
            any claims, damages, losses, and expenses (including reasonable attorneys'
            fees) arising out of or related to recording made without legally required
            consent, or any other violation of this Section.
          </p>
        </Section>

        <Section title="8. AI-generated content">
          <p>
            Lesson summaries, practice suggestions, and portfolio content are generated by
            artificial intelligence. AI output may contain errors, omissions, or inaccuracies.
            You are responsible for reviewing AI-generated content before sharing it with
            students or parents.
          </p>
          <p>
            We do not guarantee that AI-generated summaries are complete or accurate
            representations of any lesson.
          </p>
        </Section>

        <Section title="9. Privacy">
          <p>
            Our{" "}
            <PrivacyLink />{" "}
            explains how we collect, use, and protect your data. By using the Service, you agree
            to the Privacy Policy, which is incorporated into these Terms.
          </p>
        </Section>

        <Section title="10. Intellectual property">
          <p>
            Hans, Kalver Scheduler, the {companyName()} name, and all associated software, designs, and trademarks
            are owned by {companyLegalName()} These Terms do not grant you any rights to use our
            intellectual property beyond what's needed to use the Service.
          </p>
        </Section>

        <Section title="11. Disclaimers">
          <p>
            THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR
            IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE,
            OR THAT AI-GENERATED CONTENT WILL BE ACCURATE OR COMPLETE.
          </p>
        </Section>

        <Section title="12. Limitation of liability">
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, {companyLegalName().toUpperCase()} SHALL NOT BE LIABLE FOR
            ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING
            FROM YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT
            YOU PAID US IN THE 12 MONTHS PRECEDING THE CLAIM.
          </p>
        </Section>

        <Section title="13. Termination">
          <p>
            You may delete your account at any time: in the iPhone app (under Settings, or
            Profile for students), in the student portal under Profile, or by emailing{" "}
            <a href={`mailto:${supportEmail()}`} className="text-[var(--accent-yellow-text)] hover:underline">
              {supportEmail()}
            </a>
            . We may suspend or terminate your account for violations of these Terms, with
            or without notice depending on severity.
          </p>
          <p>
            Upon termination, your right to use the Service ends immediately. Sections that
            by their nature should survive termination (including Sections 5, 7, 10, 11,
            12, and 14) will do so.
          </p>
        </Section>

        <Section title="14. Governing law">
          <p>
            These Terms are governed by the laws of the State of Delaware, without regard
            to conflict of law principles. Any disputes shall be resolved in the courts of
            Delaware, and you consent to personal jurisdiction there.
          </p>
        </Section>

        <Section title="15. Changes to these Terms">
          <p>
            We may update these Terms from time to time. We'll update the date at the top
            of this page and, for material changes, notify you by email or in-app notice at
            least 14 days in advance. Continued use after changes take effect constitutes
            acceptance.
          </p>
        </Section>

        {/* Footer contact */}
        <div className="pt-10 mt-2">
          <p className="text-sm text-zinc-500">
            Questions about these Terms?{" "}
            <a href={`mailto:${supportEmail()}`} className="text-[#f4cf08] hover:underline">
              {supportEmail()}
            </a>
          </p>
        </div>
      </motion.div>
    </main>
  </PublicLayout>
);

export default TermsPage;
